/**
 * Toast Helpers Hook
 *
 * Convenience wrapper around useToast() that provides a cleaner API
 * for displaying notifications without verbose context handling.
 *
 * @example
 * const { success, error, info, warn } = useToastHelpers();
 *
 * success('Changes saved', 'Success');
 * error('Something went wrong', 'Error');
 */

import { useMemo } from 'react';
import { useToast } from '@/components/ui/ToastProvider';
import {
    toastError,
    toastInfo,
    toastSuccess,
    toastWarn,
} from '@/lib/toastHelpers.mjs';

export function useToastHelpers() {
    const toast = useToast();
    const addToast = toast?.addToast;

    return useMemo(
        () => ({
            success: (message, title) => toastSuccess(addToast, message, title),
            error: (message, title) => toastError(addToast, message, title),
            info: (message, title) => toastInfo(addToast, message, title),
            warn: (message, title) => toastWarn(addToast, message, title),
        }),
        [addToast],
    );
}
